import type { EarTrainingClip, EarTrainingMode } from '../types/earTraining';
import { parseSolfegeToken, solfegeTokenToMidi } from './earTrainingSolfegeTokens';

/** Chromatic movable-do order from do (major keys). */
export const SOLFEGE_PALETTE_ORDER_MAJOR = [
  'do',
  'di',
  'ra',
  're',
  'ri',
  'me',
  'mi',
  'fa',
  'fi',
  'se',
  'sol',
  'si',
  'te',
  'le',
  'la',
  'li',
  'ti',
] as const;

/** Chromatic movable-do order from la (minor keys). */
export const SOLFEGE_PALETTE_ORDER_MINOR = [
  'la',
  'li',
  'ti',
  'te',
  'do',
  'di',
  'ra',
  're',
  'ri',
  'me',
  'mi',
  'fa',
  'fi',
  'se',
  'sol',
  'si',
  'le',
] as const;

export function getSolfegePaletteOrder(mode: EarTrainingMode): readonly string[] {
  return mode === 'minor' ? SOLFEGE_PALETTE_ORDER_MINOR : SOLFEGE_PALETTE_ORDER_MAJOR;
}

function normalizeToken(s: string): string {
  return s.trim().toLowerCase();
}

/** Compare two tokens: palette syllable order, then octave (′ / ↓). */
export function compareSolfegeTokens(
  a: string,
  b: string,
  mode: EarTrainingMode = 'major'
): number {
  const pa = parseSolfegeToken(a);
  const pb = parseSolfegeToken(b);
  const order = getSolfegePaletteOrder(mode);
  const ia = order.indexOf(pa.syllable as (typeof order)[number]);
  const ib = order.indexOf(pb.syllable as (typeof order)[number]);
  const rankA = ia === -1 ? 99 : ia;
  const rankB = ib === -1 ? 99 : ib;
  if (rankA !== rankB) return rankA - rankB;
  return pa.octaveOffset - pb.octaveOffset;
}

/** Palette button order by pitch in this clip (low → high), not syllable name. */
export function orderSolfegePaletteTokens(
  tokens: Iterable<string>,
  clip: EarTrainingClip
): string[] {
  const uniq = [...new Map([...tokens].map(t => [normalizeToken(t), normalizeToken(t)])).values()];
  return uniq.sort((a, b) => {
    const ma = solfegeTokenToMidi(a, clip);
    const mb = solfegeTokenToMidi(b, clip);
    if (ma != null && mb != null && ma !== mb) return ma - mb;
    if (ma != null && mb == null) return -1;
    if (ma == null && mb != null) return 1;
    return compareSolfegeTokens(a, b, clip.mode);
  });
}
